/**
 * PATH: packages/email-hub/src/feature/reader-header/lanes/CompanyLane.tsx
 * FILE_ID: EMAILHUB-READERHEADER-LANES-COMPANY-001
 * PURPOSE: One identity summary for the sender — who this email is really from.
 * ROLE IN ARCHITECTURE: Bounded lane leaf of the expanded triage context.
 * MAIN EXPORTS: CompanyLane.
 * NON-RESPONSIBILITIES: No identity re-derivation (SenderIdentityCard
 *   predicates own that), no menu rendering, no drawer ownership.
 * NOTES FOR FUTURE AI: Reuses the LIVE SenderIdentityCard / CompanyChip /
 *   AddToCrmChip wiring transplanted from ReaderSignalStrip — those encode
 *   paid-for CRM gating fixes. Never fork them into lane-local copies.
 */
import SenderIdentityCard, {
  shouldOfferManualCrmFiling,
  shouldShowSenderIdentity,
} from '../../components/SenderIdentityCard'
import { AddToCrmChip } from '../../components/AddToCrmChip'
import { CompanyChip } from '../../components/CompanyChip'
import { cn } from '../../../_internal/lib/utils'
import type { Email } from '../../hooks/useEmails'
import { LANE_CONTROL_CLASS } from './laneControl'
import { resolveIdentitySummary } from './laneModel'
import { LaneShell, type LaneShellProps } from './LaneShell'

export interface CompanyLaneProps
  extends Pick<
    LaneShellProps,
    'surfaceProps' | 'onOpenEllipsis' | 'expand' | 'className'
  > {
  /** Identity-enriched email (emailWithSenderIdentity output). */
  email: Email
  onRefetch?: () => void
  onOpenCompanyContext: () => void
  onShowCompanyEmails?: () => void
  /** Full-reader embedding can intentionally suppress CRM identity. */
  hideSenderIdentity?: boolean
}

/**
 * Renders the Company lane: exactly one identity summary plus role overflow.
 *
 * Inputs: identity-enriched email and host callbacks.
 * Output: identity card / company chip / Add-to-CRM chip (the proven
 * precedence) with a "+N" roles badge for ambiguous identities.
 * Failure mode: fully unknown sender renders the muted "Unknown sender"
 * text — the lane is never blank (touch ellipsis still opens the menu).
 */
export function CompanyLane({
  email,
  surfaceProps,
  onOpenEllipsis,
  onRefetch,
  onOpenCompanyContext,
  onShowCompanyEmails,
  hideSenderIdentity = false,
  expand,
  className,
}: CompanyLaneProps) {
  const summary = resolveIdentitySummary(email)
  const extraRoles = summary.roles.length - 1

  const identity = hideSenderIdentity ? null : shouldShowSenderIdentity(email) ? (
    // ``compact`` renders a FLAT chip (no border/fill) so the lane shows one
    // row of chips, not a card nested in a card. The old className override
    // forced wrapping to survive the bordered pill; a flat chip truncates
    // instead, which keeps every lane one row tall.
    <SenderIdentityCard email={email} onProfileCreated={onRefetch} compact />
  ) : email.company_label ? (
    /* Primary click preserves reading context in the CRM-proxy drawer. */
    <CompanyChip
      label={email.company_label}
      domain={email.company_domain}
      onOpenContext={onOpenCompanyContext}
      onShowAll={onShowCompanyEmails}
    />
  ) : shouldOfferManualCrmFiling(email) ? (
    /* Unknown-but-fileable → one-tap relationship picker (AddToCrmChip). */
    <AddToCrmChip email={email} onFiled={onRefetch} />
  ) : (
    <span className="text-[11px] text-muted-foreground">Unknown sender</span>
  )

  return (
    <LaneShell
      lane="company"
      surfaceProps={surfaceProps}
      onOpenEllipsis={onOpenEllipsis}
      expand={expand}
      className={className}
      trailing={
        extraRoles > 0 ? (
          <button
            type="button"
            data-testid="company-roles-overflow"
            title={`Also: ${summary.roles.slice(1).join(', ')}`}
            onClick={onOpenCompanyContext}
            className={cn(LANE_CONTROL_CLASS, 'bg-muted text-muted-foreground hover:bg-accent')}
          >
            +{extraRoles} role{extraRoles > 1 ? 's' : ''}
          </button>
        ) : null
      }
    >
      {identity}
    </LaneShell>
  )
}
